import { useMemo, useRef, useState } from 'react';
import { FindingsGrid } from './FindingsGrid';
import { DetectorSummary } from './DetectorSummary';
import { ReadinessCard } from './ReadinessCard';
import {
  Alert,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '../../api/endpoints';
import { AdminPageLayout } from '../../components/common/AdminPageLayout';
import { DetailCard } from '../../components/common/DetailCard';

import { useAuth } from '../../auth/AuthProvider';
import { cardIcon } from '../../components/common/cardIcons';
import { ParentHoldsNothingNotice } from '../../components/common/ParentHoldsNothingNotice';


/**
 * What the anomaly detector concluded — the shadow-mode surface.
 *
 * ⚠️ **CANDIDATES, not incidents, and the page says so at the top.** Nothing
 * here has been published: no event raised, no incident opened, no
 * recommendation generated, nobody notified. A list of findings with no such
 * statement would let a reader assume somebody had been told — which is the
 * single most misleading thing this screen could do, because the natural
 * response to "vehicle X is unusual" is to believe it is already in somebody's
 * queue.
 *
 * ⚠️ **The OUTCOME MIX comes FIRST, above the findings.** On any real tenant
 * most candidates are ordinary, cold-started or gated. A screen leading with
 * the publishable ones would report a detector finding things while hiding
 * that it declined to judge most of what it looked at — and the refusals are
 * what shadow mode exists to read.
 */
/**
 * ⚠️ `1` means TODAY, not today and yesterday — the server reads `days - 1`
 * back from now. An off-by-one here makes every window silently one day wider
 * than its label.
 */
const WINDOWS = [
  { days: 1, label: 'Today' },
  { days: 7, label: 'Last 7 days' },
  { days: 14, label: 'Last 14 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

const CUSTOM = 'custom';
const today = () => new Date().toISOString().slice(0, 10);


export function AnomalyPage(): JSX.Element {
  const { tenantId } = useAuth();
  const [choice, setChoice] = useState<number | typeof CUSTOM>(7);
  const findingsRef = useRef<HTMLDivElement | null>(null);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());

  /*
   * ⚠️ The custom range is only SENT when both ends are set and ordered. The
   * server refuses one end on its own — "from the 3rd" could mean to today or to
   * the end of retention — and a request fired mid-typing would answer a
   * question nobody asked.
   */
  const custom = choice === CUSTOM && from !== '' && to !== '' && from <= to;
  const query = custom
    ? { from, to, limit: 100 }
    : { days: choice === CUSTOM ? 7 : choice, limit: 100 };

  /*
   * ⚠️ The SAME window, handed to the list, so the cards and the rows can never
   * describe different periods. `limit` is the overview's own cap and has no
   * meaning for a paged list, so it is dropped rather than passed and ignored.
   */
  const windowQuery = useMemo(
    () => (custom ? { from, to } : { days: choice === CUSTOM ? 7 : choice }),
    [custom, from, to, choice],
  );

  /*
   * ⚠️ A 403 means the tenant is not entitled, which the page already handles —
   * so this never retries and never surfaces an error of its own. It decorates
   * the page; it must not be able to break it.
   */
  const families = useQuery({
    queryKey: ['anomaly-families', tenantId],
    queryFn: () => endpoints.anomalyFamilies(),
    enabled: Boolean(tenantId),
    retry: false,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['fleet-anomalies', tenantId, query],
    queryFn: () => endpoints.fleetAnomalies(query),
    enabled: Boolean(tenantId) && (choice !== CUSTOM || custom),
  });

  return (
    <AdminPageLayout
      title="Anomaly detection"
      description="What the detector concluded from each vehicle’s own history — and what it declined to judge."
      actions={
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            select
            size="small"
            label="Window"
            value={choice}
            onChange={(e) =>
              setChoice(e.target.value === CUSTOM ? CUSTOM : Number(e.target.value))
            }
            sx={{ minWidth: 160 }}
          >
            {WINDOWS.map((w) => (
              <MenuItem key={w.days} value={w.days}>
                {w.label}
              </MenuItem>
            ))}
            <MenuItem value={CUSTOM}>Custom range…</MenuItem>
          </TextField>
          {choice === CUSTOM && (
            <>
              {/*
                ⚠️ `shrink` on both, because a date input always renders a
                placeholder — without it the label sits on top of `dd/mm/yyyy`.
                The same `displayEmpty` family of fault this codebase records
                twice for selects.
              */}
              <TextField
                type="date"
                size="small"
                label="From"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: to || undefined }}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: from || undefined, max: today() }}
              />
            </>
          )}
        </Stack>
      }
    >
      {/*
        ⚠️ An organisation holds no vehicles (D2), so it can hold no anomalies.
        Without this the page reads "no findings" at a parent, which is an
        absence presented as a measurement — the fault this notice exists for,
        reported seven times before it was applied everywhere.
      */}
      <ParentHoldsNothingNotice
        what="anomalies"
        filtered={false}
        empty={(data?.findings.length ?? 0) === 0}
      />

      {/*
        ⚠️ FIRST on the page and never dismissible. `published` is on the
        response rather than assumed here, so the day the switch is thrown this
        banner goes away because the SERVER said so.
      */}
      {data && !data.published && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Nothing here has been published.</strong> These are candidates the detector
          scored while running in shadow mode — no incident has been opened, no recommendation
          generated and nobody has been notified. They are shown so the detector can be judged
          before it is switched on.
        </Alert>
      )}

      {/*
        ⚠️ On an error the cards are NOT rendered. Two empty bordered boxes under
        an error banner read as "the detector looked and found nothing", which is
        the opposite of "we could not ask it" — the same absence-versus-failure
        distinction the rest of this page is built on.
      */}
      {/*
        ⚠️ **A window reaching before the detector ever ran is NOT a quiet
        period.** Observations exist only for days the job has run, so a range
        into the past returns an empty list that reads exactly like "nothing was
        unusual" — and only one of those is a fact about the fleet. Named, with
        the boundary, rather than left to be inferred from a short table.
      */}
      {data && data.earliestObserved !== null && data.from < data.earliestObserved && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nothing was recorded before <strong>{data.earliestObserved}</strong>, so the part of
          this window before that date is a period nobody looked at — not a quiet one.
        </Alert>
      )}
      {data && data.earliestObserved === null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The detector has not recorded anything for this branch yet, so an empty list here says
          nothing about whether anything unusual happened.
        </Alert>
      )}

      {/*
        ⚠️ **A muted kind is SILENCE with a reason, and without this the reader
        cannot tell it from a quiet fleet.** The findings grid lists what was
        found; a kind switched off produces nothing, and nothing looks exactly
        like nothing. Named here rather than on every empty row, and it links to
        the place the choice was made.
      */}
      {families.data && families.data.families.some((f) => f.muted) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {families.data.families.filter((f) => f.muted).length} of{' '}
          {families.data.families.length} kinds are switched off for this organisation —{' '}
          {families.data.families
            .filter((f) => f.muted)
            .map((f) => f.label)
            .join(', ')}
          . Those days are still measured and still listed below; they simply raise no
          incident. Change it in Tenant settings.
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          Could not load the detector’s conclusions. This says nothing about whether anything
          unusual happened — the request failed.
        </Alert>
      )}

      {!error && (
      <Stack spacing={2}>
        <DetailCard title="What the detector looked at" icon={cardIcon('diagnostics')}>
          {isLoading && <Typography color="text.secondary">Reading…</Typography>}
          {data && <DetectorSummary data={data} />}
        </DetailCard>

        {/*
          ⚠️ **The gates of design §19.0b, and they are what shadow mode is
          FOR.** A stage that stores observations and measures nothing is a stage
          nobody can exit: until these were on a screen the only way to ask "is
          this ready for a customer" was SQL, which means in practice nobody
          asked.
        */}
        {data && (
          <DetailCard
            title="Is it ready?"
            subtitle="Whether this detector is worth trusting yet, and whether anybody is reading it"
            icon={cardIcon('chart')}
            /* ⚠️ The findings live on this page, so the footer's control
               SCROLLS rather than navigating — a link back to the same screen
               would lose the window the reader had chosen. */
          >
            <ReadinessCard
              metrics={data.metrics}
              onViewFindings={() =>
                findingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            />
          </DetailCard>
        )}

        <Box ref={findingsRef}>
          <FindingsGrid overview={data} window={windowQuery} />
        </Box>
      </Stack>
      )}
    </AdminPageLayout>
  );
}

